import React, { useState, Fragment, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table,
  Container,
  Card,
  CardTitle,
  Button,
  Badge,
  Row,
  Col,
  FormGroup,
  Input,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Dropdown,
} from "reactstrap";
import { getInvoiceList } from "../../../redux/actions";
import {
  convertToUSDateTimeFormat,
  getAlertToast,
  getConfirm,
  getCurrentAgencyId,
} from "../../../utils/functions/CustomFunctions";
import CustomTableEmptyRecord from "../../common/CustomTableEmptyRecord";
import CustomTableHeader from "../../common/CustomTableHeader";
import CustomTableLoader from "../../common/CustomTableLoader";
import Pagination from "../../common/Pagination";
import { debounce } from "underscore";
import DatePicker from "react-datepicker";
import moment from "moment";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  AGENCY_NEWINVOICE_PAGE,
} from "../../../utils/AppRoutes";
import { Link } from "react-router-dom";
import CustomViewButton from "../../common/CustomViewButton";
import CustomDeleteButton from "../../common/CustomDeleteButton";
import CustomEditButton from "../../common/CustomEditButton";
import { authHeader } from "../../../utils/functions/CoreFunctions";
import { DELETE_INVOICE_URL } from "../../../utils/APIEndpoints";
import Swal from "sweetalert2";
import API from "../../../redux/actions/API";

//table Headers
const tableColumns = [
  { title: "Invoice Number" },
  { title: "Customer" },
  { title: "Email" },
  { title: "Amount" },
  { title: "Payment Due Date" },
  { title: "Location Code" },
  { title: "Status" },
  { title: "Actions" },
];

const headers = [
  { label: "Invoice Number", key: "invoiceNumber" },
  { label: "Customer", key: "customerName" },
  { label: "Email", key: "email" },
  { label: "Amount", key: "paymentAmount" },
  { label: "Payment Due Date", key: "invoiceExpiryDate" },
  { label: "Location Code", key: "locationCode" },
  { label: "Status", key: "invoiceStatus" },
];

const initialFilterValues = {
  customerName: "",
  invoiceNumber: "",
  email: "",
  invoiceStatus: "",
  invoiceExpiryDate: "",
  count: 10,
};

export default function InvoiceList() {
  const dispatch = useDispatch();
  const currentAgencyId = getCurrentAgencyId();

  const [currentPage, setCurrentPage] = useState(0);
  const [filter, setFilter] = useState(initialFilterValues);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [date, setDate] = useState("");
  const [dropdownOpen, setdropdownOpen] = useState(false);

  const getDownloadFileName = () => {
    return "Invoices-" + convertToUSDateTimeFormat(new Date());
  };
  //onPageChanged
  const onPageChanged = (page) => {
    setCurrentPage(page - 1);
    dispatch(
      getInvoiceList({
        ...filter,
        offset: page - 1,
        agencyId: currentAgencyId,
      })
    );
  };

  //filter
  const filterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  //Onchange filter
  const changeFilterField = ({ target: { name, value } }) => {
    setFilter({ ...filter, [name]: value });
  };

  const toggle = () => {
    setdropdownOpen(!dropdownOpen);
  };

  //handleReset
  const handleReset = () => {
    setFilter((val) => ({
      ...initialFilterValues,
      count: val.count,
    }));
    setDate("");
  };

  //Date Change
  const handleDateChange = (date) => {
    setDate(date || "");
    let formattedDate = date ? moment(date).format("MM/DD/YYYY") : "";
    setFilter({ ...filter, ["invoiceExpiryDate"]: formattedDate });
  };

  //Download PDF
  const downloadPdf = () => {
    const doc = new jsPDF("P", "px", "a4");
    doc.autoTable({
      didDrawPage: function (data) {
        doc.text(`Invoices - ${convertToUSDateTimeFormat(new Date())}`, 15, 15);
      },
      body: listData,
      columns: [
        { header: "Invoice Number", dataKey: "invoiceNumber" },
        { header: "Customer", dataKey: "customerName" },
        { header: "Email", dataKey: "email" },
        { header: "Amount", dataKey: "paymentAmount" },
        { header: "Payment Due Date", dataKey: "invoiceExpiryDate" },
        { header: "Location Code", dataKey: "locationCode" },
        { header: "Status", dataKey: "invoiceStatus" },
      ],
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 80 },
        2: { cellWidth: 120 },
        3: { cellWidth: 40 },
        4: { cellWidth: 50 },
        5: { cellWidth: 40 },
        6: { cellWidth: 40 },
      },
      margin: {
        top: 30,
        right: 8,
        bottom: 15,
        left: 8,
      },
      rowPageBreak: "avoid",
      theme: "grid",
    });
    doc.save(getDownloadFileName() + ".pdf");
  };

  //Delete Invoice
  const deleteInvoice = async (id) => {
    try {
      //setLoading(true);
      const options = {
        method: "DELETE",
        url: DELETE_INVOICE_URL,
        headers: authHeader(),
        params: { invoiceMasterId: id },
      };
      const response = await API(options);
      const { status } = response.data;
      if (status === "Success") {
        Swal.fire(getAlertToast("success", "Record removed successfully"));
        dispatch(
          getInvoiceList({
            ...filter,
            offset: currentPage,
            agencyId: currentAgencyId,
          })
        );
      }
    } catch (error) {
      if (error.response) {
        let { data } = error.response;
        Swal.fire(getAlertToast("error", data.message));
      }
    } finally {
      //setLoading(false);
    }
  };

  //Delete Record
  const deleteRecord = async (id, e) => {
    Swal.fire(
      getConfirm("warning", "Are you sure do you want to delete?")
    ).then((result) => {
      if (result.isConfirmed) {
        deleteInvoice(id);
      }
    });
  };

  const handleRowPerPage = ({ target: { value, name } }) => {
    setCurrentPage(0);
    setFilter((val) => ({ ...val, [name]: value || undefined }));
  };

  //Debounce
  const delayedQuery = useRef(
    debounce(
      (filter) =>
        dispatch(
          getInvoiceList({
            ...filter,
            offset: currentPage,
            agencyId: currentAgencyId,
          })
        ),
      500
    )
  ).current;

  useEffect(() => {
    delayedQuery(filter);
  }, [filter]);

  const {
    loading,
    list: listData = [],
    totalCount = 0,
  } = useSelector((state) => state.InvoiceListReducer);
  const {
    customerName = "",
    email = "",
    invoiceNumber = "",
    invoiceStatus = "",
  } = filter;

  const statusLabelColor = {
    Draft: "secondary",
    Open: "primary",
    Paid: "success",
    Closed: "danger",
  };


  return (
    <Fragment>
      <Container fluid className="mt-5">
        <Card body>
          <div>
            <CardTitle className="mb-4 f-l w-fit">All Invoices</CardTitle>

            <i
              className="fas fa-filter"
              style={{
                float: "right",
                margin: "4px 7px 3px 16px",
                fontSize: "19px",
                padding: "3px",
                cursor: "pointer",
              }}
              onClick={filterToggle}
            ></i>
            <div className="top-btn" style={{ float: "right" }}>
              <Dropdown isOpen={dropdownOpen} toggle={toggle}>
                <DropdownToggle color="danger" caret>
                  Export
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem onClick={downloadPdf}>PDF</DropdownItem>
                  <DropdownItem>
                    <CSVLink
                     className="link"
                      key={`csv_${listData.length}`}
                      data={listData}
                      headers={headers}
                      filename={`${getDownloadFileName()}.csv`}
                      target="_blank"
                    >
                      CSV
                    </CSVLink>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
            <Link to={AGENCY_NEWINVOICE_PAGE}>
              <Button className="f-r" color="primary" style={{ marginTop: 0 }}>
                New
              </Button>
            </Link>
          </div>

          {isFilterOpen && (
            <Row form className="mb-4" style={{ marginTop: "1rem" }}>
              <Col md={3}>
                <FormGroup>
                  <Input
                    type="text"
                    name="invoiceNumber"
                    placeholder="Invoice Number"
                    onChange={changeFilterField}
                    value={invoiceNumber}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Input
                    type="text"
                    name="customerName"
                    placeholder="Customer Name"
                    onChange={changeFilterField}
                    value={customerName}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Input
                    type="text"
                    name="email"
                    placeholder="Email"
                    onChange={changeFilterField}
                    value={email}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <DatePicker
                    style={{
                      zIndex: "9999 !important",
                      width: "100% !important",
                    }}
                    selected={date}
                    onChange={handleDateChange}
                    dateFormat="MM/dd/yyy"
                    placeholderText="Invoice Expiry Date"
                    className="form-control mb-3"
                    width="100%"
                    popperPlacement="bottom"
                    popperModifiers={{
                      flip: {
                        behavior: ["bottom"],
                      },
                      preventOverflow: {
                        enabled: false,
                      },
                      hide: {
                        enabled: false,
                      },
                    }}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Input
                    type="select"
                    name="invoiceStatus"
                    placeholder="Status"
                    onChange={changeFilterField}
                    value={invoiceStatus}
                  >
                    <option value={""}>-- Show All Status --</option>
                    <option value={"Draft"}>Draft</option>
                    <option value={"Open"}>Open</option>
                    <option value={"Paid"}>Paid</option>
                    <option value={"Closed"}>Closed</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={2}>
                <div style={{ marginTop: "4px" }}>
                  <Button color="danger" onClick={handleReset}>
                    Reset
                  </Button>
                </div>
              </Col>
            </Row>
          )}

          <Table className="invoice-table">
            <CustomTableHeader tableColumns={tableColumns} />
            <tbody>
              {loading ? (
                // Table Loader
                <CustomTableLoader columnsCount={tableColumns.length} />
              ) : (
                <>
                  {!listData.length ? (
                    //  Empty Record
                    <CustomTableEmptyRecord
                      columnsCount={tableColumns.length}
                    />
                  ) : (
                    listData.map(
                      (
                        {
                          invoiceMasterId,
                          invoiceNumber,
                          customerName,
                          email,
                          invoiceStatus,
                          invoiceExpiryDate,
                          paymentAmount,
                          locationCode,
                        },
                        key
                      ) => (
                        <tr key={key}>
                          <td>{invoiceNumber || "-"}</td>
                          <td>{customerName || "-"}</td>
                          <td>{email || "-"}</td>
                          <td>{paymentAmount}</td>
                          <td>{invoiceExpiryDate || "-"}‎</td>
                          <td>{locationCode}</td>
                          <td>
                            <Badge className="invoice-status" color={statusLabelColor[invoiceStatus]}>
                              {invoiceStatus}
                            </Badge>
                          </td>
                          <td>
                            <CustomViewButton
                              keyvalue={key}
                              tag={Link}
                              to={`/view-invoice/${invoiceMasterId}`}
                              target="_blank"
                            />

                            <CustomDeleteButton
                              keyvalue={key}
                              onClick={(e) => deleteRecord(invoiceMasterId, e)}
                            />

                            {invoiceStatus === "Draft" && (
                              <CustomEditButton
                                keyvalue={key}
                                tag={Link}
                                to={`/new-invoice/draft/${invoiceMasterId}`}
                                target="_blank"
                              />
                            )}
                          </td>
                        </tr>
                      )
                    )
                  )}
                </>
              )}
            </tbody>
          </Table>

          {listData.length ? (
            <div className="d-flex py-4 justify-content-end space_between">
              <div className="space_between">
                <div>
                  <p className="per_page">Records/Page</p>
                </div>
                <div>
                  <FormGroup>
                    <Input
                      className="pagi_drop"
                      type="select"
                      name="count"
                      onChange={handleRowPerPage}
                      value={filter.count}
                    >
                      <option value={""}>Show All</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </Input>
                  </FormGroup>
                </div>
              </div>
              {filter.count && (
                <div>
                  <Pagination
                    itemClass="page-item"
                    linkClass="page-link"
                    activePage={currentPage + 1}
                    itemsCountPerPage={filter.count}
                    totalItemsCount={totalCount}
                    onChange={onPageChanged}
                  />
                </div>
              )}
            </div>
          ) : (
            ""
          )}
        </Card>
      </Container>
    </Fragment>
  );
}
